/**
 * Formatting helpers — must be used everywhere a number, currency, or date
 * is rendered for human consumption (UI, PDF, email).
 *
 * Spec format requirements:
 *   - Numbers: thousands separator = ",", decimal = ".", 2 decimals for currency
 *   - Currency display: ISO code BEFORE amount, separated by non-breaking space
 *     e.g. "CHF 1,234.50", "USD 42.00"
 *   - Dates: ISO 8601 throughout — "2026-04-25"
 *   - Datetimes: "2026-04-25 14:30"
 */
const NBSP = '\u00A0';
/**
 * Format a number with comma thousands and dot decimal, fixed to `decimals`.
 * Always shows trailing zeros. Negative numbers retain the minus sign.
 */
export function formatNumber(value, decimals = 2) {
    if (!Number.isFinite(value))
        return 'NaN';
    // Intl.NumberFormat with 'en-US' gives us comma thousands + dot decimal.
    return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true,
    });
}
/**
 * Format a currency amount as "ISO\u00A0amount" — e.g. "CHF 1,234.50".
 * Always 2 decimals.
 */
export function formatCurrency(amount, currencyCode) {
    return `${currencyCode}${NBSP}${formatNumber(amount, 2)}`;
}
/**
 * Swiss-style number: apostrophe thousands separator, dot decimal —
 * e.g. "16'981.55", "-19'737.40". Derived from `formatNumber` (en-US) and
 * swapping the comma for a straight apostrophe, so the separator is always a
 * plain `'` (not the curly `’`/NBSP some `de-CH` ICU builds emit).
 */
export function formatNumberSwiss(value, decimals = 2) {
    return formatNumber(value, decimals).replace(/,/g, "'");
}
/**
 * Swiss-style currency — "ISO amount" with apostrophe thousands,
 * e.g. "CHF 16'981.55". Used in the Credit Card Submission UI. The
 * comma-style `formatCurrency` remains the default for the other tools and
 * for all PDFs/emails (per the global format spec).
 */
export function formatCurrencySwiss(amount, currencyCode) {
    return `${currencyCode}${NBSP}${formatNumberSwiss(amount, 2)}`;
}
/**
 * Format an FX rate to 6 decimal places without thousands separator.
 * (Rates rarely exceed 1000, but capping decimals matches storage precision.)
 */
export function formatFxRate(rate) {
    return rate.toLocaleString('en-US', {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
        useGrouping: false,
    });
}
/**
 * ISO date — "YYYY-MM-DD" — based on UTC components.
 *
 * Use this for: storage, FX rate lookups, filenames, audit log payloads.
 * For user-facing display in a particular locale, the UI should use the
 * user's local timezone — but the ISO format is always UTC-derived to avoid
 * cross-timezone drift in the data layer.
 */
export function formatISODate(d) {
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/** ISO datetime — "YYYY-MM-DD HH:MM" — based on UTC. */
export function formatISODateTime(d) {
    const date = formatISODate(d);
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${date} ${hours}:${minutes}`;
}
/** Month names — used in PDF titles, filenames, "March 2026". */
export const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
export function monthName(month) {
    if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}`);
    }
    return MONTH_NAMES[month - 1];
}
/**
 * Round a number to N decimals using IEEE 754 / `toFixed` semantics.
 *
 * Uses `toFixed()` + `parseFloat()` to round at the runtime's decimal
 * serialization layer. This is "as correct as JS Numbers can be."
 *
 * Caveat — IEEE 754 representation:
 *   The literal `1.005` is stored as 1.00499999...; `toFixed(2)` therefore
 *   returns "1.00" not "1.01". This affects only inputs that are exact half-
 *   way decimals; for the values this app actually rounds (`amount × rate`
 *   products of arbitrary-precision floats already imprecisely stored), there
 *   is no consistent "correct" answer that doesn't require a decimal library.
 *
 *   At the scale this app deals with (single-receipt amounts <CHF 10k, rates
 *   to 6 dp, totals <CHF 100k per statement) the worst-case drift is ±CHF 0.01
 *   on a single line item, well within tolerance for an expense tool. Swiss
 *   tax filing rounds to whole francs anyway.
 *
 *   If the company later requires bit-exact decimal accounting, swap this
 *   implementation for big.js — call sites won't change.
 */
export function roundTo(value, decimals) {
    if (!Number.isFinite(value))
        return value;
    return parseFloat(value.toFixed(decimals));
}
const BIRTHDAY_MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
/** 'YYYY-MM-DD' → 'MM-DD' (day + month only, no year). Null if absent/invalid.
 *  Used to expose birthdays without revealing the birth year. */
export function birthdayMMDD(dob) {
    if (!dob)
        return null;
    const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(dob.trim());
    return m ? `${m[1]}-${m[2]}` : null;
}
/** 'YYYY-MM-DD' or 'MM-DD' → e.g. '14 Sep' (day + month, never the year). */
export function formatBirthday(v) {
    if (!v)
        return null;
    const s = v.trim();
    const full = /^\d{4}-(\d{2})-(\d{2})$/.exec(s);
    const short = /^(\d{2})-(\d{2})$/.exec(s);
    const mm = full ? Number(full[1]) : short ? Number(short[1]) : NaN;
    const dd = full ? Number(full[2]) : short ? Number(short[2]) : NaN;
    if (!(mm >= 1 && mm <= 12) || !(dd >= 1 && dd <= 31))
        return null;
    return `${dd} ${BIRTHDAY_MONTHS[mm - 1]}`;
}
//# sourceMappingURL=format.js.map